import { Smartphone } from "lucide-react";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import DocsCategoryLayout from "../components/docs/DocsCategoryLayout";
import { Steps, Tip } from "../components/docs/DocsBits";

const ARTICLES = [
  {
    id: "installing",
    title: "Installing the technician app",
    body: (
      <>
        <p>The Fieldz AI mobile app is purpose-built for technicians in the field. Light, fast, focused on the three things they do all day: check schedule, do the job, get paid.</p>
        <h3>Direct download links</h3>
        <ul>
          <li><strong>iOS</strong> — <a href="https://apps.apple.com/us/app/fieldz-ai/id6759849207" target="_blank" rel="noopener noreferrer">App Store</a> (iPhone, iPad). Requires iOS 14+.</li>
          <li><strong>Android</strong> — <a href="https://play.google.com/store/apps/details?id=com.fieldzai.app" target="_blank" rel="noopener noreferrer">Google Play</a>. Requires Android 8+.</li>
        </ul>
        <h3>SMS invite shortcut</h3>
        <p>The fastest way to onboard a tech is to invite them from <strong>Settings &gt; Team</strong>. They get an SMS with a deep link that:</p>
        <ol>
          <li>Opens the App Store / Play Store directly to the Fieldz AI app.</li>
          <li>After install, the app opens with their email pre-filled — they just set a password and they&apos;re in.</li>
        </ol>
        <Tip>Skip telling techs to &quot;go to the app store and search&quot;. Use the SMS invite — there&apos;s zero typing for them.</Tip>
      </>
    ),
  },
  {
    id: "what-techs-can-do",
    title: "What technicians can do in the app",
    body: (
      <>
        <p>The technician app is intentionally narrow — it shows only what a tech needs to work. No CRM, no admin settings, no billing access.</p>
        <h3>Full feature list</h3>
        <ul>
          <li><strong>View schedule</strong> — today&apos;s and upcoming jobs with full details (address, customer, notes, line items).</li>
          <li><strong>Update job status</strong> — Start, Pause, Complete. Owners get realtime updates on the dashboard.</li>
          <li><strong>Attach photos</strong> — capture from camera or pick from library, attached directly to the job for documentation.</li>
          <li><strong>Call masking</strong> — call or SMS the customer through your business number; their personal number stays private.</li>
          <li><strong>Invoice / payment</strong> — create or accept payment on an invoice from the field.</li>
          <li><strong>Earnings tracker</strong> — personal earnings and tips by day/week/month.</li>
          <li><strong>Push notifications</strong> — new assignments, schedule changes, customer replies.</li>
          <li><strong>Navigation</strong> — tap an address to launch Maps with one-tap routing.</li>
        </ul>
        <h3>What techs CAN&apos;T do</h3>
        <p>Techs explicitly cannot edit team, billing, business settings, or other techs&apos; jobs. This is intentional — it keeps the app focused and prevents accidental damage to your settings.</p>
        <Tip>If a tech needs to modify a job they didn&apos;t create (rare), have a Dispatcher or Owner make the change from the web dashboard. The tech sees the update in realtime via push.</Tip>
      </>
    ),
  },
  {
    id: "offline-mode",
    title: "Offline mode",
    body: (
      <>
        <p>Field service work happens in basements, parking garages, and rural areas with patchy reception. The mobile app is built to keep working when the network isn&apos;t.</p>
        <h3>What&apos;s cached locally</h3>
        <ul>
          <li>Today&apos;s and tomorrow&apos;s scheduled jobs, including customer details and addresses.</li>
          <li>Notes, line items, and photos already attached.</li>
          <li>The tech&apos;s personal earnings dashboard.</li>
        </ul>
        <h3>How sync works when connection restores</h3>
        <Steps
          items={[
            <>Status changes (Start / Pause / Complete) made while offline are queued in a local buffer.</>,
            <>Photos taken offline are queued with the photo data.</>,
            <>The app polls for connectivity every ~15 seconds. When it&apos;s back, the queue flushes — every change syncs in order.</>,
            <>You&apos;ll see a small <em>Syncing…</em> indicator in the header during the flush. Once it disappears, everything is up to date.</>,
          ]}
        />
        <h3>Known limitations</h3>
        <ul>
          <li>You can&apos;t create a brand-new job offline (those need to come from the dispatch side anyway).</li>
          <li>Call masking requires connectivity — the PIN bridge is a Twilio call, which needs a live network. Use a personal call as fallback if you&apos;re truly offline.</li>
          <li>Photos taken offline aren&apos;t available to other users until sync completes.</li>
        </ul>
        <Tip>If a tech&apos;s phone is consistently offline (e.g. underground parking), have them tap &quot;Start&quot; before they walk in. The app caches the job; status updates will sync when they come back up.</Tip>
      </>
    ),
  },
  {
    id: "mobile-troubleshooting",
    title: "Troubleshooting the mobile app",
    body: (
      <>
        <h3>Push notifications not arriving</h3>
        <ul>
          <li><strong>Device permissions.</strong> iOS: <em>Settings &gt; Notifications &gt; Fieldz AI &gt; Allow</em>. Android: <em>Settings &gt; Apps &gt; Fieldz AI &gt; Notifications &gt; On</em>.</li>
          <li><strong>In-app permission.</strong> Open the app, go to <strong>Settings &gt; Notifications</strong>, and confirm push is enabled.</li>
          <li><strong>Re-register.</strong> Sign out and back in. This re-registers the device&apos;s push token with our server.</li>
          <li><strong>Battery optimization.</strong> On Android, aggressive battery optimization (especially Samsung &amp; Xiaomi) can suspend background services. Add Fieldz AI to the &quot;protected apps&quot; or &quot;not optimized&quot; list.</li>
        </ul>
        <h3>App not loading jobs</h3>
        <ul>
          <li>Verify the tech&apos;s account is <em>Active</em> in <strong>Settings &gt; Team</strong> (web).</li>
          <li>If the schedule shows empty but jobs exist, force-quit the app and relaunch — sometimes the local cache gets stuck.</li>
          <li>Check connectivity: airplane mode + back is faster than rebooting.</li>
        </ul>
        <h3>Calling customer not working</h3>
        <ul>
          <li>Call masking requires the job to be <em>Assigned to this tech</em>, not just created. PINs are generated only on assignment.</li>
          <li>The tech&apos;s phone number must be saved in their team profile in E.164 format.</li>
          <li>If everything looks right but the call still fails, the tech can fall back to a normal call from their personal phone — just toggle off masking on that one call from the job detail page.</li>
        </ul>
        <Tip>If multiple techs are reporting the same issue, it&apos;s likely a server-side or carrier issue, not their device. Check our <a href="https://status.fieldzai.com" target="_blank" rel="noopener noreferrer">status page</a> first.</Tip>
      </>
    ),
  },
];

const DocsMobilePage = () => (
  <DocsCategoryLayout
    category={{
      id: "mobile",
      name: "Mobile App",
      icon: Smartphone,
      intro: "Install the technician app, learn what's offline-capable, and fix the most common mobile issues.",
    }}
    articles={ARTICLES}
  />
);

export default DocsMobilePage;
